import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter
} from "../../../../components/ui/Dialog";
import Button from "../../../../components/ui/Button";
import Select from "../../../../components/ui/Select";
import Input from "../../../../components/ui/Input";

export interface EntityFormData {
  entityName: string;
  country: string;
  numOfEmployees: string;
  payCycle: string;
}

interface EntityDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: EntityFormData) => void;
  editData?: EntityFormData | null;
  options: {
    entityNames: string[];
    countries: string[];
    employeesNum: string[];
    payCycles: string[];
  };
}

export default function EntityDialog({ open, onClose, onSubmit, editData, options }: EntityDialogProps) {
  const { t } = useTranslation();

  const [entityName, setEntityName] = useState("");
  const [country, setCountry] = useState("");
  const [numOfEmployees, setNumOfEmployees] = useState("");
  const [payCycle, setPayCycle] = useState("");

  useEffect(() => {
    if (editData) {
      setEntityName(editData.entityName);
      setCountry(editData.country);
      setNumOfEmployees(editData.numOfEmployees);
      setPayCycle(editData.payCycle);
    } else {
      setEntityName("");
      setCountry("");
      setNumOfEmployees("");
      setPayCycle("");
    }
  }, [editData, open]);

  const lastPayrollTime = new Date();

  const toOptions = (arr: string[]) => arr.map(v => ({ value: v, label: v }));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{editData ? t("Edit") : t("New Entity")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Select
            label={t("Entity Name")}
            options={toOptions(options.entityNames)}
            value={entityName}
            onChange={(val: any) => setEntityName(val || "")}
            searchable
          />
          <Select
            label={t("Country")}
            options={toOptions(options.countries)}
            value={country}
            onChange={(val: any) => setCountry(val || "")}
            searchable
          />
          <Select
            label={t("# Of Employees")}
            options={toOptions(options.employeesNum)}
            value={numOfEmployees}
            onChange={(val: any) => setNumOfEmployees(val || "")}
            searchable
          />
          <Select
            label={t("Pay Cycle")}
            options={toOptions(options.payCycles)}
            value={payCycle}
            onChange={(val: any) => setPayCycle(val || "")}
            searchable
          />
          <Input
            label={t("Last Payroll Plan")}
            disabled
            value={lastPayrollTime.toLocaleDateString()}
          />
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("Cancel")}</Button>
          <Button onClick={() => onSubmit({ entityName, country, numOfEmployees, payCycle })}>
            {editData ? t("Edit") : t("Add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
